import React, {
    FormEvent,
    useCallback,
    useDeferredValue,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ReactFocusLock from "react-focus-lock";
import { ProductSearchBuilder, ProductSearchResponse } from "@relewise/client";
import { useDebounce } from "react-use";
import { useAppData } from "@danishagro/shared/src/contexts/appData.context";
import { useTranslations } from "@danishagro/shared/src/contexts/translations/translations.context";
import { DA_Icon, DA_IconNames } from "@danishagro/shared/src/components/atoms/Icon/Icon.component";
import { useOnClickOutside } from "@danishagro/shared/src/hooks/useOnClickOutside.hook";
import { blurActiveElement } from "@danishagro/shared/src/helpers/blurActiveElement.helper";
import { useBottomSheet } from "@danishagro/shared/src/contexts/bottomSheet/bottomSheet.context";
import { useSiteHeader } from "@danishagro/shared/src/contexts/siteHeader.context";
import { DA_Spinner } from "@danishagro/shared/src/components/atoms/Spinner/Spinner.component";
import { useRelewise } from "@danishagro/shared/src/contexts/relewise.context";
import { addWebUserGroupsFilter } from "@danishagro/shared/src/helpers/relewiseFilters.helper";
import { DA_Text } from "@danishagro/shared/src/components/atoms/Text/Text.component";
import {
    DA_EmptyState,
    DA_EmptyStateTypes,
} from "@danishagro/shared/src/components/molecules/EmptyState/EmptyState.component";
import { DA_ButtonLink } from "@danishagro/shared/src/components/atoms/ButtonLink/ButtonLink.component";
import { useProductPrices } from "@danishagro/shared/src/hooks/useProductPrices.hook";
import { adjustUrl } from "@danishagro/shared/src/helpers/adjustUrl.helper";
import { cn } from "@danishagro/shared/src//helpers/classNames.helper";
import { _SearchProduct } from "../SearchInput/SearchProduct/SearchProduct.component";
import { RelewiseSearchProduct } from "../SearchInput/interfaces/relewiseSearchProduct.interface";
// eslint-disable-next-line css-modules/no-unused-class
import S from "../SearchInputB2c/SearchInputB2c.module.scss";

interface DA_SearchInputB2bStyleProps {
    trapFocus?: boolean;
    smallHeight?: boolean;
    autoFocus?: boolean;
}

const DA_SearchInputB2bStyle = (props: DA_SearchInputB2bStyleProps) => {
    const [searchTerm, setSearchTerm] = useState("");
    const deferredSearchTerm = useDeferredValue(searchTerm);
    const [viewState, setViewState] = useState<string>("NONE");
    const [relewiseResults, setRelewiseResults] = useState<RelewiseSearchProduct[]>();
    const [totalResults, setTotalResults] = useState(0);
    const { currentSite, customerNumber, cvrAndCustomerNumbers, showPrices } = useAppData();
    const { getDictionaryString } = useTranslations();
    const location = useLocation();
    const wrapperRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const controllerRef = useRef<AbortController | null>();
    const navigate = useNavigate();

    const [focusedIndex, setFocusedIndex] = useState(0);
    const [hasMoved, setHasMoved] = useState<boolean>(false);

    const listRefProduct = useRef<HTMLUListElement | null>(null);

    const { dismissBottomSheet } = useBottomSheet();
    const { togglePopUp } = useSiteHeader();

    const formRef = useRef<HTMLFormElement>(null);

    const { getSearcher, getDefaultSettings, resetFilters } = useRelewise();

    const clearSearch = useCallback(() => {
        setSearchTerm("");
        setTotalResults(0);
        setViewState("NONE");
    }, []);

    const onResultClicked = useCallback(() => {
        clearSearch();

        dismissBottomSheet();
        togglePopUp(undefined);
    }, [clearSearch, dismissBottomSheet, togglePopUp]);

    const reset = useCallback(() => {
        clearSearch();
        inputRef.current.value = "";
    }, [clearSearch]);

    useEffect(() => {
        if (inputRef.current.value === "") {
            setFocusedIndex(0);
            setHasMoved(false);
        }
    }, [searchTerm]);

    // Auto-focus the input when component mounts if autoFocus prop is true
    useEffect(() => {
        if (props.autoFocus && inputRef.current) {
            // Small delay to ensure the bottom sheet animation has started
            const timeoutId = setTimeout(() => {
                inputRef.current?.focus();
            }, 100);
            return () => clearTimeout(timeoutId);
        }
    }, [props.autoFocus]);

    const onFocus = useCallback(() => {
        if (inputRef.current?.value) {
            setSearchTerm(inputRef.current.value);
        }
    }, []);

    useOnClickOutside(
        wrapperRef,
        useCallback(() => {
            reset();
            setTimeout(() => blurActiveElement(), 10);
        }, [reset]),
        [totalResults > 0]
    );

    const [announcement, setAnnouncement] = useState("");
    const [productResultsCount, setProductResultsCount] = useState<number | null>(null);

    useDebounce(
        async () => {
            if (controllerRef.current) {
                controllerRef.current.abort();
            }

            if (deferredSearchTerm) {
                setViewState("LOADING");
                controllerRef.current = new AbortController();

                const searchBuilder = new ProductSearchBuilder(getDefaultSettings())
                    .setSelectedProductProperties({
                        displayName: true,
                        pricing: true,
                        allData: true,
                        dataKeys: ["*"],
                        allVariants: true,
                    })
                    .setSelectedVariantProperties({ allData: true })
                    .setExplodedVariants(1)
                    .setTerm(deferredSearchTerm);

                if (globalThis.useCustomerGroups) {
                    searchBuilder.filters((f) => {
                        addWebUserGroupsFilter(f, customerNumber, cvrAndCustomerNumbers);
                    });
                }

                getSearcher()
                    .searchProducts(searchBuilder.build())
                    .then((response: ProductSearchResponse) => {
                        if (response && response.results.length > 0) {
                            const count = response?.results?.length || 0;
                            setRelewiseResults(response.results as RelewiseSearchProduct[]);
                            setTotalResults(response.hits);
                            setProductResultsCount(count);
                        } else {
                            setRelewiseResults([]);
                            setTotalResults(0);
                        }
                    })
                    .catch((err) => {
                        if (err?.name !== "AbortError") {
                            setViewState("ERROR");
                            console.error("Relewise search error:", err);
                        }
                    })
                    .finally(() => {
                        setViewState("DONE");
                    });
            } else {
                setViewState("NONE");
                setTotalResults(0);
            }
        },
        250,
        [deferredSearchTerm]
    );

    useEffect(() => {
        if (productResultsCount !== null) {
            const productText =
                productResultsCount > 0
                    ? productResultsCount > 1
                        ? `${getDictionaryString(
                              `accessibility.screenReader.searchOnProducts.moreThanOneResult`,
                              {
                                  productResults: productResultsCount,
                              }
                          )}`
                        : `${getDictionaryString(
                              `accessibility.screenReader.searchOnProducts.oneResult`,
                              {
                                  productResults: productResultsCount,
                              }
                          )}`
                    : getDictionaryString(`accessibility.screenReader.searchOnProducts.noResults`);

            setAnnouncement(productText);
            setProductResultsCount(null);
        }
    }, [getDictionaryString, productResultsCount]);

    useEffect(() => {
        reset();
    }, [location, reset]);

    const searchPlaceholderText = useMemo(() => {
        switch (currentSite) {
            case "MYFARM":
                return getDictionaryString("searchPlaceholderMyfarm");
            case "B2B":
                return getDictionaryString("searchPlaceholderB2b");
            default:
                return getDictionaryString("searchPlaceholder");
        }
    }, [currentSite, getDictionaryString]);

    const EmptyState = () => (
        <DA_EmptyState
            type={DA_EmptyStateTypes.NoResults}
            title={getDictionaryString("general.noResults.title")}
            text={getDictionaryString("general.noResults.text")}
        />
    );

    const redirectToSearchPage = useCallback(() => {
        // Dismiss bottom sheet and close overlays before navigation
        dismissBottomSheet();
        togglePopUp(undefined);

        // Reset filters before navigating to clear facet state
        resetFilters();

        // MYFARM doesn't have its own search results page, redirect to B2B
        if (currentSite === "MYFARM") {
            const b2bSearchUrl = `${
                globalThis.b2bFrontendUrl
            }searchresults?term=${encodeURIComponent(searchTerm)}`;
            window.location.href = b2bSearchUrl;
        } else {
            // B2B and B2C have their own search results pages
            // Navigate with only the term parameter (no facets)
            navigate(`/searchresults?term=${encodeURIComponent(searchTerm)}`, { replace: true });
        }
        reset();
    }, [currentSite, navigate, searchTerm, reset, dismissBottomSheet, togglePopUp, resetFilters]);

    const onSubmit = useCallback(
        (event: FormEvent<HTMLFormElement>) => {
            event.preventDefault();
            if (searchTerm) {
                redirectToSearchPage();
            }
        },
        [redirectToSearchPage, searchTerm]
    );

    const displayedProducts = relewiseResults?.slice(0, 10);

    const priceItems = useMemo(
        () =>
            showPrices
                ? relewiseResults
                      ?.map((p) => ({ productNumber: p.data?.masterProductNumber?.value }))
                      .filter((item) => !!item.productNumber) ?? []
                : [],
        [showPrices, relewiseResults]
    );

    const prices = useProductPrices(priceItems);

    const [urlState, setUrlState] = useState<string | null>(null);

    const navigateWithKeys = useCallback(
        (event: React.KeyboardEvent) => {
            if (event.key === "Escape") {
                reset();
                setHasMoved(false);
                setFocusedIndex(0);
                return;
            }

            if (viewState !== "DONE") return;

            const productCount = displayedProducts?.length || 0;

            const updateUrlFromCurrentFocus = (index: number) => {
                const listElement = listRefProduct.current;
                if (!listElement) return;

                const listItems = listElement.querySelectorAll("li");
                const item = listItems[index];
                const link = item?.querySelector("a");
                const href = link?.getAttribute("href");

                if (href) {
                    const url = new URL(href, window.location.href);
                    const pathname = url.pathname;
                    setUrlState(pathname);
                }
            };

            if (event.key === "ArrowDown") {
                event.preventDefault();

                if (!hasMoved) {
                    setHasMoved(true);
                    if (productCount > 0) {
                        setFocusedIndex(0);
                        updateUrlFromCurrentFocus(0);
                    }
                    return;
                }

                setFocusedIndex((prev) => {
                    const next = prev + 1;
                    const newIndex = next >= productCount ? 0 : next;
                    updateUrlFromCurrentFocus(newIndex);
                    return newIndex;
                });
            }

            if (event.key === "ArrowUp") {
                event.preventDefault();

                if (!hasMoved) {
                    setHasMoved(true);
                    if (productCount > 0) {
                        setFocusedIndex(productCount - 1);
                        updateUrlFromCurrentFocus(productCount - 1);
                    }
                    return;
                }

                setFocusedIndex((prev) => {
                    const next = prev - 1;
                    const newIndex = next < 0 ? productCount - 1 : next;
                    updateUrlFromCurrentFocus(newIndex);
                    return newIndex;
                });
            }

            if (event.key === "Enter") {
                if (hasMoved && urlState) {
                    event.preventDefault();
                    onResultClicked();
                    navigate(urlState);
                    reset();
                    setTimeout(() => blurActiveElement(), 10);
                }
            }
        },
        [viewState, displayedProducts?.length, reset, hasMoved, urlState, onResultClicked, navigate]
    );

    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (formRef.current && !formRef.current.contains(event.target as Node)) {
                setFocusedIndex(0);
                setHasMoved(false);
            }
        }

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    // Navigate to the first variant that has a discount
    const getDiscountedVariantUrl = useCallback(
        (product: RelewiseSearchProduct): string | null => {
            const productNumber = product.data?.masterProductNumber?.value;
            const price = prices?.find((p) => p?.productNumber === productNumber);
            const variantId = price?.variantDiscounts?.[0];
            if (!variantId) return null;

            const baseUrl = product.data?.canonicalBaseUrl?.value;
            if (!baseUrl) return null;

            const slug = product?.allVariants?.find((v) => v.variantId === variantId)?.data
                ?.webNameSlug?.value;

            return slug ? adjustUrl(`${baseUrl}/${slug}`) : null;
        },
        [prices]
    );

    return (
        <ReactFocusLock disabled={!props.trapFocus || totalResults === 0 || false}>
            {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
            <div className={S.wrapper} ref={wrapperRef} onKeyDown={navigateWithKeys}>
                <div
                    role="status"
                    aria-live="polite"
                    style={{ position: "absolute", left: "-9999px", height: 0, overflow: "hidden" }}
                >
                    {announcement}
                </div>
                <form role="search" className={S.form} ref={formRef} onSubmit={onSubmit}>
                    <input
                        type="search"
                        name="term"
                        className={cn(S.input, props.smallHeight && S.inputSmallHeight)}
                        placeholder={searchPlaceholderText}
                        onChange={({ target }) => setSearchTerm(target.value)}
                        onFocus={onFocus}
                        autoComplete="off"
                        ref={inputRef}
                    />

                    {deferredSearchTerm ? (
                        <button
                            type="button"
                            className={S.button}
                            onClick={reset}
                            aria-label={getDictionaryString("accessibility.search.clearSearch")}
                        >
                            <DA_Icon name={DA_IconNames.Close} className={S.clearIcon} />
                        </button>
                    ) : (
                        <button
                            type="button"
                            className={S.button}
                            onClick={() => inputRef.current?.focus()}
                            tabIndex={-1}
                            aria-label={getDictionaryString("accessibility.search.searchButton")}
                        >
                            <DA_Icon name={DA_IconNames.Zoom} className={S.searchIcon} />
                        </button>
                    )}
                </form>

                <div className={S.incrementalSearchResults}>
                    {viewState === "DONE" ? (
                        <div className={cn(S.resultsContainer, S.searchNoContent)}>
                            <div className={S.resultsColumn2}>
                                <div className={S.resultColumnHeader}>
                                    <DA_Text noPadding smallText>
                                        {getDictionaryString("searchInput.sectionProductsTitle")}
                                    </DA_Text>
                                    <DA_ButtonLink
                                        href={`/searchresults?term=${searchTerm}`}
                                        className={S.showAllItems}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            redirectToSearchPage();
                                        }}
                                        title={getDictionaryString("searchInput.viewAllProducts")}
                                    />
                                </div>
                                <ul className={S.productList} ref={listRefProduct}>
                                    {displayedProducts?.length > 0 ? (
                                        displayedProducts?.map((product, index) => {
                                            const isFocused = hasMoved && focusedIndex === index;

                                            return (
                                                <li
                                                    key={product.productId}
                                                    className={isFocused ? S.focused : ""}
                                                    role="option"
                                                    aria-selected={focusedIndex === index}
                                                >
                                                    <_SearchProduct
                                                        highlightedString={searchTerm}
                                                        onResultClicked={onResultClicked}
                                                        discountedVariantUrl={getDiscountedVariantUrl(
                                                            product
                                                        )}
                                                        {...product}
                                                    />
                                                </li>
                                            );
                                        })
                                    ) : (
                                        <EmptyState />
                                    )}
                                </ul>
                            </div>
                        </div>
                    ) : null}

                    {/** Loading */}
                    {viewState === "LOADING" ? (
                        <div className={S.statusContainer}>
                            <DA_Spinner />
                        </div>
                    ) : null}

                    {/** Error */}
                    {viewState === "ERROR" ? (
                        <div className={S.statusContainer}>
                            {getDictionaryString("general.searchError")}
                        </div>
                    ) : null}
                </div>
            </div>
        </ReactFocusLock>
    );
};

export default DA_SearchInputB2bStyle;
